import { HTTP_INTERCEPTORS, HttpBackend, HttpClient, HttpClientModule } from '@angular/common/http'
import { APP_INITIALIZER, NgModule } from '@angular/core'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { AppService, AppServiceModule } from '@app/app.service'
import { ConfigService } from '@core/config.service'
import { CoreModule } from '@core/core.module'
import { DataService } from '@data/service/data.service'
import { EventService } from '@data/service/event.service'
import { ProjectService } from '@data/service/project.service'
import { UserService } from '@data/service/user.service'
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { KeycloakBearerInterceptor, KeycloakService } from 'keycloak-angular'
import { QuillModule } from 'ngx-quill'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { StorageModule } from './data/store/storage.module'

@NgModule({
   declarations: [AppComponent],
   imports: [
      AppServiceModule,
      BrowserModule,
      BrowserAnimationsModule,
      CoreModule.forRoot(),
      AppRoutingModule,
      StorageModule,
      QuillModule.forRoot(),
      HttpClientModule,
      TranslateModule.forRoot({
         defaultLanguage: 'en-US',
         useDefaultLang: true,
         loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [HttpBackend],
         },
      }),
   ],
   exports: [BrowserModule, CoreModule, TranslateModule, HttpClientModule, QuillModule],
   providers: [
      AppService,
      DataService,
      UserService,
      ProjectService,
      EventService,
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [TranslateService],
         useFactory: initializeTranslation,
      },
      {
         provide: APP_INITIALIZER,
         multi: true,
         deps: [ConfigService],
         useFactory: (configService: ConfigService) => {
            return () => configService.initRuntimeConfig()
         },
      },
      {
         provide: APP_INITIALIZER,
         useFactory: initializeKeycloak,
         multi: true,
         deps: [KeycloakService, HttpClient],
      },
      {
         provide: HTTP_INTERCEPTORS,
         useClass: KeycloakBearerInterceptor,
         multi: true,
      },
      KeycloakService,
   ],
   bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(handler: HttpBackend) {
   return new TranslateHttpLoader(new HttpClient(handler), './assets/i18n/')
}

function initializeTranslation(translate: TranslateService) {
   return () => {
      translate.setDefaultLang('en-US')
      const browserLang = navigator.language
      // Usa en-US se la lingua del browser non è supportata
      const langToUse = browserLang === 'en-US' ? browserLang : 'en-US'
      return translate.use(langToUse).toPromise()
   }
}

function initializeKeycloak(keycloak: KeycloakService, httpClient: HttpClient) {
   return () => {
      const tenantUrl = window.location.hostname
      console.log(`Tenant Url: ${tenantUrl}`)

      return httpClient
         .get<any>(`assets/configs/properties.json?ts=${Date.now()}`)
         .toPromise()
         .then((properties) => {
            let kcConfig: any = undefined

            switch (tenantUrl) {
               case 'private.ai-cube.eu':
                  kcConfig = properties.tenants.find((t) => t.name === 'ai-cube').keycloak
                  break
               case 'private.alsical.eu':
                  kcConfig = properties.tenants.find((t) => t.name === 'alsical').keycloak
                  break
               case 'private.assure.ngi.eu':
                  kcConfig = properties.tenants.find((t) => t.name === 'assure-ngi').keycloak
                  break
               case 'private.autoship-project.eu':
                  kcConfig = properties.tenants.find((t) => t.name === 'autoship').keycloak
                  break
               case 'private.carbon4pur.eu':
                  kcConfig = properties.tenants.find((t) => t.name === 'carbon4pur').keycloak
                  break
               case 'private.sisal-pilot.eu':
                  kcConfig = properties.tenants.find((t) => t.name === 'sisal-pilot').keycloak
                  break
               case 'ateam-project.eu':
                  kcConfig = properties.tenants.find((t) => t.name === 'ateam').keycloak
                  break
               default:
                  kcConfig = properties.tenants.find((t) => t.name === 'default').keycloak
            }

            return keycloak.init({
               config: {
                  url: kcConfig.authUrl,
                  realm: kcConfig.realm,
                  clientId: kcConfig.clientId,
               },
               initOptions: {
                  enableLogging: true,
                  checkLoginIframe: false,
                  flow: 'standard',
                  onLoad: 'check-sso',
                  responseMode: 'query',
               },
               enableBearerInterceptor: true,
               bearerPrefix: 'Bearer ',
            })
         })
   }
}

interface KeycloakConfig {
   keycloak: {
      authUrl: string
      realm: string
      clientId: string
   }
}

